import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { sanitizeDom } from '../../../utils'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'
import { usePlanChangeFlag } from '../../../hooks/usePlanChange'
import NavLinkWithQuery from '../../shared/nav-link-with-query'
import styles from './cta-box.css'
import EmailBox from '../../shared/emailBox'
import CTABoxHeaderImage from '../hotspot-header-image/hotspot-header-image'

function CtaBox({ content, isChangeBg, headerImageUri }) {
  const {
    line1, line2, line3, cta, isRental, isGuestHome,
    msgForBasicDiscount, msgForStandardDiscount, msgForAnnualDiscount,
    titleTreatmentImage
  } = content
  const flags = useFeatureFlags(['discount_basic', 'discount_standard', 'discount_annual'])
  const showBasicDiscount = flags.discount_basic
  const showStandardDiscount = flags.discount_standard
  const showAnnualDiscount = flags.discount_annual
  const isShowingDiscount = showBasicDiscount || showStandardDiscount || showAnnualDiscount

  const planDetails = usePlanChangeFlag()
  let plan = planDetails.basic
  let msgForDiscountOffer = ''
  if (isChangeBg && isShowingDiscount) {
    if (showAnnualDiscount) {
      plan = planDetails.annual
      msgForDiscountOffer += `${msgForDiscountOffer ? '<br />' : ''}${msgForAnnualDiscount}`
    }
    if (showStandardDiscount) {
      plan = planDetails.standard
      msgForDiscountOffer += `${msgForDiscountOffer ? '<br />' : ''}${msgForStandardDiscount}`
    }
    if (showBasicDiscount) {
      plan = planDetails.basic
      msgForDiscountOffer += `${msgForDiscountOffer ? '<br />' : ''}${msgForBasicDiscount}`
    }
  }

  const [priceBeforeDot, priceAfterDot] = isShowingDiscount ? plan.discountPrice?.split('.') : plan.price.split('.')

  return (
    <div className={classNames(styles.wrapper, isChangeBg && styles.wrapperInCenter)}>
      <CTABoxHeaderImage headerImageUri={headerImageUri} />

      {titleTreatmentImage ? (
        <img src={titleTreatmentImage} alt="Title Treatment" className={styles.titleTreatment} />
      ) : (
        <div className={classNames(styles.title, isChangeBg && styles.largeTitle)}>{line1}</div>
      )}

      {line2 && (
        <div
          className={styles.synopsis}
          dangerouslySetInnerHTML={{ __html: sanitizeDom(line2) }}
        />
      )}

      {msgForDiscountOffer && (
        <div
          className={styles.synopsis}
          dangerouslySetInnerHTML={{ __html: sanitizeDom(msgForDiscountOffer) }}
        />
      )}

      {isChangeBg && (
        <>
          <div className={styles.priceInfo}>
            {isShowingDiscount ? (
              <div className={styles.originalPrice}>
                &nbsp;$
                {plan.price}
                {plan.unit}
                &nbsp;
              </div>
            ) : (
              <div>From</div>
            )}
          </div>

          <div className={styles.priceAmount}>
            <span className={styles.currency}>$</span>
            <span className={styles.priceAmountBeforeDot}>{priceBeforeDot}</span>
            <div className={styles.priceAmountRightFrame}>
              <span className={styles.priceAmountAfterDot}>{priceAfterDot}</span>
              <span className={styles.priceAmountUnit}>{plan.unit}</span>
            </div>
          </div>
        </>
      )}

      {isGuestHome ? (
        <EmailBox buttonText="GET STARTED" inputText="Email" />
      ) : (
        <div className={styles.buttonGroup}>
          {cta.text && (
            <NavLinkWithQuery
              data-e2e={
                isRental
                  ? 'cta-box-rental-slide-button'
                  : 'cta-box-slide-start-watching-button'
              }
              className={classNames(styles.button, {
                [styles.isRental]: isRental
              })}
              to={cta.url}
            >
              {cta.text}
            </NavLinkWithQuery>
          )}
          <div className={styles.wordsUnderButton}>{line3}</div>
        </div>
      )}
    </div>
  )
}

CtaBox.propTypes = {
  content: PropTypes.shape({
    line1: PropTypes.string.isRequired,
    line2: PropTypes.string.isRequired,
    line3: PropTypes.string.isRequired,
    msgForBasicDiscount: PropTypes.string,
    msgForStandardDiscount: PropTypes.string,
    msgForAnnualDiscount: PropTypes.string,
    cta: PropTypes.shape({
      url: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    }),
    isRental: PropTypes.bool,
    isGuestHome: PropTypes.bool,
    titleTreatmentImage: PropTypes.string
  }).isRequired,
  isChangeBg: PropTypes.bool.isRequired,
  headerImageUri: PropTypes.string
}

export default CtaBox
